<template>
  <el-dialog
    v-model="dialogVisible"
    class="character-dialog"
    width="520px"
    align-center
    :show-close="false"
    :close-on-click-modal="false"
    modal-class="character-dialog-mask"
    @closed="handleClosed"
  >
    <div
      class="character-modal-shell"
      :class="{
        'is-generation': generationStep === 'preview',
        'is-text-mode': generationStep === 'form' && appearanceTab === 'text',
        'is-image-mode': generationStep === 'form' && appearanceTab === 'image'
      }"
    >
      <div class="character-modal-header">
        <div class="character-modal-heading">
          <h2 class="character-modal-title">{{ isEdit ? '编辑角色' : '创建角色' }}</h2>
        </div>
        <div class="character-modal-header-actions">
          <button
            v-if="showMembershipUpgrade"
            class="membership-upgrade-button"
            type="button"
            @click="emit('upgrade')"
          >
            <SvgIcon class="membership-upgrade-icon" name="membership-crown" />
            <span>升级会员生成7折</span>
          </button>
          <button
            class="character-modal-close"
            type="button"
            aria-label="关闭弹窗"
            @click="dialogVisible = false"
          >
            <SvgIcon class="close-icon" name="close-line" />
            <SvgIcon class="collapse-icon" name="character-chevron-down" />
          </button>
        </div>
      </div>

      <template v-if="generationStep === 'form'">
        <div class="character-modal-tabs">
          <SegmentedTabs
            class="character-generation-tabs"
            :model-value="appearanceTab"
            :tabs="GENERATION_TABS"
            aria-label="角色生成方式"
            @update:model-value="handleAppearanceTabChange"
          />
        </div>

        <el-scrollbar class="character-modal-body">
          <div class="character-modal-body-content">
            <div class="character-modal-field name-field">
              <label class="character-modal-label" for="character-name-input">
                角色名称<span class="required">*</span>
              </label>
              <el-input
                id="character-name-input"
                v-model="form.name"
                class="character-name-input"
                placeholder="请输入角色名称，如：甜甜"
              />
            </div>

            <div v-if="appearanceTab === 'text'" class="character-modal-field appearance-field">
              <label class="character-modal-label" for="character-description-input">
                <span class="desktop-copy">输入灵感</span>
                <span class="mobile-copy">输入灵感</span>
                <span class="required">*</span>
              </label>
              <el-input
                id="character-description-input"
                v-model="form.description"
                class="character-description-input"
                type="textarea"
                resize="none"
                placeholder="描述角色外貌, 如: 短发、大眼睛、蓝色条纹上衣"
              />
            </div>

            <div
              v-if="appearanceTab === 'image'"
              class="character-modal-field image-upload-field"
              :class="{ 'has-uploaded-image': form.refImageUrl }"
            >
              <label class="character-modal-label image-upload-label"
                >上传照片<span class="required">*</span></label
              >
              <el-upload
                ref="imageUploadRef"
                class="character-image-upload"
                :class="{ 'is-uploaded': form.refImageUrl, 'is-uploading': uploadingImage }"
                :action="CHARACTER_REFERENCE_IMAGE_UPLOAD_ENDPOINT"
                :auto-upload="false"
                :show-file-list="false"
                :disabled="uploadingImage"
                accept="image/*"
                drag
                :on-change="handleUploadChange"
              >
                <div class="upload-content" :class="{ 'has-image': form.refImageUrl }">
                  <template v-if="uploadingImage">
                    <span class="upload-icon-bubble upload-loading-bubble">
                      <SvgIcon class="upload-loading-icon" name="spinner" />
                    </span>
                    <span class="upload-caption">图片上传中...</span>
                  </template>
                  <template v-else-if="form.refImageUrl">
                    <img class="uploaded-preview-image" :src="form.refImageUrl" alt="参考图片" />
                    <el-button class="change-upload-button" native-type="button">更换</el-button>
                    <el-button
                      class="remove-upload-button"
                      native-type="button"
                      aria-label="移除上传照片"
                      @click.stop.prevent="clearUploadImage"
                    >
                      <SvgIcon class="remove-upload-icon" name="character-upload-delete" />
                    </el-button>
                  </template>
                  <template v-else>
                    <span class="upload-icon-bubble">
                      <SvgIcon name="upload-photo" />
                    </span>
                    <span class="upload-caption">
                      <span class="desktop-copy">点击上传照片，支持 JPG / PNG</span>
                      <span class="mobile-copy">点击上传照片</span>
                    </span>
                  </template>
                </div>
              </el-upload>
            </div>

            <div v-if="appearanceTab === 'image'" class="mobile-image-examples">
              <h3 class="mobile-section-title">示例</h3>
              <div class="mobile-example-grid" aria-label="照片示例">
                <div
                  v-for="example in MOBILE_IMAGE_EXAMPLES"
                  :key="example.label"
                  class="mobile-example-card"
                  :class="{ 'is-wide': example.wide }"
                >
                  <span
                    class="mobile-example-thumb"
                    :class="{ 'is-blurry': example.blurred, 'is-wide': example.wide }"
                  >
                    <img :src="example.image" :alt="example.label" />
                  </span>
                  <span class="mobile-example-caption" :class="{ invalid: !example.valid }">
                    <SvgIcon :name="example.valid ? 'check' : 'cross'" />
                    <span>{{ example.label }}</span>
                  </span>
                </div>
              </div>
            </div>

            <div v-if="appearanceTab === 'image'" class="mobile-image-divider" />

            <div
              v-if="appearanceTab === 'image'"
              class="character-modal-field supplement-description-field"
            >
              <label
                class="character-modal-label supplement-description-label"
                for="character-supplement-description-input"
              >
                <span class="supplement-description-title">
                  补充描述<span class="optional-tag">（非必填）</span>
                </span>
                <span class="supplement-description-tip">
                  保留原照片主要特征，仅调整指定细节
                </span>
              </label>
              <el-input
                id="character-supplement-description-input"
                v-model="form.description"
                class="character-description-input supplement-description-input"
                type="textarea"
                resize="none"
                placeholder="想修改发型、服装、配饰等，可以在这里告诉 AI"
              />
            </div>

            <div class="character-modal-field style-field">
              <label class="character-modal-label">形象风格<span class="required">*</span></label>
              <div class="style-picker-strip" aria-label="形象风格">
                <button
                  v-for="style in ART_STYLES"
                  :key="style.value"
                  class="style-card"
                  :class="{ selected: form.artStyle === style.value }"
                  type="button"
                  :aria-pressed="form.artStyle === style.value"
                  @click="form.artStyle = style.value"
                >
                  <span class="style-thumb">
                    <img :src="style.image" :alt="style.label" />
                  </span>
                  <span class="style-label">
                    <span class="desktop-copy">{{ style.label }}</span>
                    <span class="mobile-copy">{{ style.mobileLabel }}</span>
                  </span>
                </button>
              </div>
            </div>
          </div>
        </el-scrollbar>

        <div class="character-modal-footer">
          <el-button
            class="generate-character-button"
            type="primary"
            :disabled="!canGenerate || generating || uploadingImage"
            :loading="saving"
            @click="emit('save')"
          >
            <span class="desktop-copy">AI 生成角色</span>
            <span class="mobile-copy">生成AI角色</span>
            <SvgIcon class="ai-cost-icon" name="ai-cost" />
            <span class="credit-count desktop-copy">{{ generationCost }}</span>
            <span class="credit-count mobile-copy">{{ generationCost }}</span>
          </el-button>
        </div>
      </template>

      <div v-else class="character-generation-body">
        <div class="generated-preview-card" :class="{ 'is-loading': generating }">
          <img
            v-if="form.generatedUrl && !generating"
            class="generated-preview-image"
            :src="form.generatedUrl"
            :alt="`${form.name || '角色'}生成图`"
          />
          <div v-else class="generation-loading-state">
            <CharacterLoadingIcon class="generation-loading-icon" />
            <span class="generation-loading-text">角色正在生成中...</span>
            <span class="generation-loading-help">你可以先填写角色名称与设定内容</span>
          </div>
        </div>

        <div class="generation-fields">
          <div class="character-modal-field generation-name-field">
            <label class="character-modal-label" for="generation-character-name-input">
              角色名称<span class="required">*</span>
            </label>
            <el-input
              id="generation-character-name-input"
              v-model="form.name"
              class="character-name-input"
              placeholder="输入角色名称，例如：甜甜"
            />
          </div>

          <div class="character-modal-field generation-setting-field">
            <label
              class="character-modal-label generation-setting-label"
              for="generation-character-description-input"
            >
              <span class="generation-setting-title"
                >角色介绍<span class="required desktop-required">*</span></span
              >
              <span class="generation-setting-tip desktop-copy">
                <SvgIcon class="generation-setting-tip-icon" name="book-click-guide-tip" />
                <span>帮助AI认识角色，生成更合适的故事</span>
              </span>
              <span class="generation-setting-tip mobile-copy">（帮助AI认识角色）</span>
              <span class="required mobile-required">*</span>
            </label>
            <el-input
              id="generation-character-description-input"
              v-model="form.setting"
              class="character-description-input generation-description-input"
              type="textarea"
              resize="none"
              placeholder="描述角色身份和特点，例如：男孩、女孩、动物等以及年龄、性格特点 
参考： 8岁的小男孩，喜欢恐龙，勇敢又善良"
            />
            <div class="generation-actions">
              <el-button
                class="regenerate-character-button"
                :disabled="generating || saving"
                @click="handleRedescribe"
              >
                重新生成形象
              </el-button>
              <el-button
                class="confirm-generation-button"
                type="primary"
                :disabled="saving"
                :loading="saving"
                @click="handleConfirmGeneration"
              >
                确定
              </el-button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </el-dialog>
</template>

<script setup lang="ts">
  import { computed, ref } from 'vue';
  import { ElMessage } from 'element-plus';
  import type { UploadFile, UploadInstance } from 'element-plus';
  import {
    CHARACTER_ART_STYLES,
    CHARACTER_DEFAULT_ART_STYLE,
    CHARACTER_NO_STYLE_VALUE
  } from '@/constants/characterArtStyles';
  import exampleClearFace from '@/assets/mock/character-example-clear-face.png';
  import exampleCoverFace from '@/assets/mock/character-example-face-covered.png';
  import exampleHalfBody from '@/assets/mock/character-example-half-body.png';
  import exampleAnimal from '@/assets/mock/character-example-animal.png';
  import {
    CHARACTER_REFERENCE_IMAGE_UPLOAD_ENDPOINT,
    type CharacterItem,
    uploadCharacterReferenceImage
  } from '@/api/character';
  import SegmentedTabs from '@/components/common/tabs/SegmentedTabs.vue';
  import type { SegmentedTabItem, SegmentedTabValue } from '@/types/components';
  import SvgIcon from '@/components/common/icon/SvgIcon.vue';
  import CharacterLoadingIcon from './CharacterLoadingIcon.vue';

  type CharacterForm = Omit<CharacterItem, 'id' | 'createdAt' | 'updatedAt'> & { id?: string };
  type AppearanceTab = 'text' | 'image';

  defineOptions({
    name: 'CharacterEditorDialog'
  });

  const props = defineProps<{
    visible: boolean;
    form: CharacterForm;
    isEdit: boolean;
    saving: boolean;
    generating: boolean;
    generationStep: 'form' | 'preview';
    appearanceTab: AppearanceTab;
    canGenerate: boolean;
    canConfirmGeneration: boolean;
    showMembershipUpgrade: boolean;
    generationCost: number;
  }>();

  const emit = defineEmits<{
    'update:visible': [visible: boolean];
    'update:appearanceTab': [tab: AppearanceTab];
    save: [];
    redescribe: [];
    confirm: [];
    upgrade: [];
    closed: [];
  }>();

  const GENERATION_TABS: Array<SegmentedTabItem<AppearanceTab>> = [
    {
      value: 'image',
      iconName: 'character-image',
      iconClass: 'character-generation-tabs__icon--image',
      label: '图片生成',
      mobileLabel: '图生角色'
    },
    {
      value: 'text',
      iconName: 'character-text',
      iconClass: 'character-generation-tabs__icon--text',
      label: '文本生成',
      mobileLabel: 'AI生成'
    }
  ];

  const ART_STYLES = CHARACTER_ART_STYLES;

  const NO_STYLE_VALUE = CHARACTER_NO_STYLE_VALUE;

  const MOBILE_IMAGE_EXAMPLES: Array<{
    label: string;
    image: string;
    valid: boolean;
    blurred?: boolean;
    wide?: boolean;
  }> = [
    { label: '五官清晰', image: exampleClearFace, valid: true },
    { label: '脸部遮挡', image: exampleCoverFace, valid: false },
    { label: '半身照', image: exampleHalfBody, valid: true, wide: true },
    { label: '动物形象', image: exampleAnimal, valid: true, wide: true }
  ];

  const imageUploadRef = ref<UploadInstance>();
  const uploadingImage = ref(false);
  const latestUploadId = ref(0);

  const dialogVisible = computed({
    get: () => props.visible,
    set: (visible: boolean) => emit('update:visible', visible)
  });

  const handleAppearanceTabChange = (value: SegmentedTabValue) => {
    if (value !== 'text' && value !== 'image') return;

    switchAppearanceTab(value);
  };

  const switchAppearanceTab = (tab: AppearanceTab) => {
    if (props.form.artStyle === NO_STYLE_VALUE) {
      props.form.artStyle = CHARACTER_DEFAULT_ART_STYLE;
    }

    emit('update:appearanceTab', tab);
  };

  const handleRedescribe = () => {
    switchAppearanceTab(props.form.refImageUrl ? 'image' : 'text');
    emit('redescribe');
  };

  const handleConfirmGeneration = () => {
    if (!props.form.name.trim()) {
      ElMessage.warning('角色名称还没填写');
      return;
    }

    if (!props.form.setting.trim()) {
      ElMessage.warning('角色介绍还没填写');
      return;
    }

    emit('confirm');
  };

  const handleUploadChange = async (uploadFile: UploadFile) => {
    const file = uploadFile.raw;
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      ElMessage.warning('请上传图片格式文件');
      imageUploadRef.value?.clearFiles();
      return;
    }

    const uploadId = latestUploadId.value + 1;
    latestUploadId.value = uploadId;
    uploadingImage.value = true;

    try {
      const imageUrl = await uploadCharacterReferenceImage(file);
      if (uploadId !== latestUploadId.value) return;

      props.form.refImageUrl = imageUrl;
      ElMessage.success('图片上传成功');
    } catch (error) {
      if (uploadId === latestUploadId.value) {
        ElMessage.error({
          message: getUploadErrorMessage(error),
          duration: 6000
        });
      }
    } finally {
      if (uploadId === latestUploadId.value) {
        uploadingImage.value = false;
      }
      imageUploadRef.value?.clearFiles();
    }
  };

  const clearUploadImage = () => {
    latestUploadId.value += 1;
    uploadingImage.value = false;
    props.form.refImageUrl = '';
    imageUploadRef.value?.clearFiles();
  };

  const handleClosed = () => {
    latestUploadId.value += 1;
    uploadingImage.value = false;
    imageUploadRef.value?.clearFiles();
    emit('closed');
  };

  const getUploadErrorMessage = (error: unknown) => {
    if (error instanceof Error && error.message) return error.message;
    if (isRecord(error)) {
      const message = error.errorMsg || error.msg;
      if (typeof message === 'string' && message) return message;
    }

    return '图片上传失败，请重新上传';
  };

  const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null;
</script>

<style scoped>
  .mobile-copy {
    display: none;
  }

  .desktop-copy {
    display: inline;
  }

  .mobile-required {
    display: none;
  }

  .character-generation-tabs :deep(.character-generation-tabs__icon--text) {
    width: 16px;
    height: 16px;
    fill: currentColor;
  }

  .character-generation-tabs :deep(.character-generation-tabs__icon--image) {
    width: 14px;
    height: 14px;
    fill: none;
    stroke: currentColor;
    stroke-linecap: round;
    stroke-linejoin: round;
    stroke-width: 1.1667;
  }

  .character-generation-tabs
    :deep(.segmented-tabs__item:not(.is-active) .character-generation-tabs__icon--text) {
    color: #444e63;
  }

  .collapse-icon,
  .mobile-image-examples,
  .mobile-image-divider {
    display: none;
  }

  :global(.character-dialog-mask) {
    background: rgba(0, 0, 0, 0.45);
  }

  :global(.character-dialog.el-dialog) {
    width: min(520px, calc(100vw - 32px));
    height: 620px;
    overflow: hidden;
    padding: 0;
    border-radius: 16px;
    background: #fff;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  }

  :global(.character-dialog.el-dialog .el-dialog__body) {
    width: 100%;
    height: 620px;
    padding: 0;
    overflow: hidden;
  }

  :global(.character-dialog.el-dialog .el-dialog__header),
  :global(.character-dialog.el-dialog .el-dialog__footer) {
    display: none;
  }

  .character-modal-shell {
    display: flex;
    flex-direction: column;
    width: 520px;
    max-width: 100%;
    height: 620px;
    overflow: hidden;
    background: #fff;
  }

  .character-modal-shell.is-generation .character-modal-header {
    height: 72px;
    padding: 24px 24px 16px;
  }

  .character-modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 59px;
    box-sizing: border-box;
    padding: 24px 24px 2px;
    flex: 0 0 auto;
  }

  .character-modal-heading {
    width: 169px;
    height: 33px;
  }

  .character-modal-header-actions {
    display: flex;
    align-items: center;
    margin-left: auto;
    gap: 6px;
  }

  .membership-upgrade-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 136px;
    height: 38px;
    box-sizing: border-box;
    padding: 0 16px;
    border: 1px solid rgba(254, 230, 181, 0.18);
    border-radius: 14px;
    background: #fff8ec;
    color: #d38743;
    cursor: pointer;
    font-family: 'Source Han Sans CN VF', 'Noto Sans SC', 'Microsoft YaHei', sans-serif;
    font-size: 11px;
    font-weight: 400;
    line-height: 18px;
    gap: 7px;
    white-space: nowrap;
  }

  .membership-upgrade-button:hover,
  .membership-upgrade-button:focus-visible {
    border-color: rgba(254, 230, 181, 0.7);
    background: #fff4e3;
    outline: none;
  }

  .membership-upgrade-icon {
    width: 12px;
    height: 12px;
  }

  .character-modal-title {
    margin: 0;
    color: #160211;
    font-family: 'Source Han Sans CN VF', 'Noto Sans SC', 'Microsoft YaHei', sans-serif;
    font-size: 16px;
    font-weight: 700;
    line-height: 25px;
  }

  .character-modal-close {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    padding: 0;
    border: 0;
    border-radius: 50%;
    background: transparent;
    color: #999;
    cursor: pointer;
  }

  .character-modal-close:hover,
  .character-modal-close:focus-visible {
    background: #f5f5f5;
    outline: none;
  }

  .character-modal-close .svg-icon {
    width: 18px;
    height: 18px;
    overflow: hidden;
    fill: none;
    stroke: currentColor;
    stroke-linecap: round;
    stroke-width: 1.5;
  }

  .character-modal-tabs {
    width: 100%;
    box-sizing: border-box;
    padding: 20px 21px 0;
    flex: 0 0 auto;
  }

  .character-modal-body {
    width: 100%;
    min-height: 0;
    box-sizing: border-box;
    overflow: hidden;
    flex: 1 1 auto;
  }

  .character-modal-body-content {
    display: flex;
    flex-direction: column;
    width: 100%;
    box-sizing: border-box;
    padding: 15px 21px;
    gap: 15px;
  }

  .character-modal-field {
    display: flex;
    flex-direction: column;
    width: 100%;
    max-width: 100%;
    flex-shrink: 0;
  }

  .name-field {
    height: 69px;
  }

  .appearance-field {
    height: 90px;
  }

  .image-upload-field {
    height: 156px;
    box-sizing: border-box;
    padding-top: 2px;
  }

  .image-upload-field.has-uploaded-image {
    height: 207px;
  }

  .style-field {
    height: 146px;
  }

  .supplement-description-field {
    height: auto;
    gap: 12px;
  }

  .supplement-description-label {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: auto;
    min-height: 14px;
    padding: 0;
    gap: 8px;
  }

  .supplement-description-title {
    flex: 0 0 auto;
    color: #160211;
    font-family: 'Source Han Sans CN VF', 'Noto Sans SC', 'Microsoft YaHei', sans-serif;
    font-size: 13px;
    font-weight: 500;
    line-height: 14px;
  }

  .optional-tag {
    color: #999;
    font-weight: 400;
  }

  .supplement-description-tip {
    flex: 1 1 auto;
    color: #999;
    font-family: 'Source Han Sans CN VF', 'Noto Sans SC', 'Microsoft YaHei', sans-serif;
    font-size: 11px;
    font-weight: 400;
    line-height: 14px;
    text-align: right;
  }

  .supplement-description-input :deep(.el-textarea__inner) {
    height: 64px;
    min-height: 64px !important;
  }

  .character-modal-label {
    display: block;
    height: 26px;
    box-sizing: border-box;
    padding: 0 0 6px;
    color: #160211;
    font-family: 'Source Han Sans CN VF', 'Noto Sans SC', 'Microsoft YaHei', sans-serif;
    font-size: 13px;
    font-weight: 500;
    line-height: 19.5px;
  }

  .style-field .character-modal-label {
    height: 20px;
    padding: 0;
  }

  .image-upload-field .character-modal-label {
    height: 20px;
    padding: 0;
  }

  .required {
    color: #ef4444;
  }

  .character-name-input :deep(.el-input__wrapper) {
    width: 100%;
    height: 43px;
    min-height: 43px;
    box-sizing: border-box;
    padding: 10px 12px;
    border-radius: 14px;
    background: #fff;
    box-shadow: inset 0 0 0 1px #6033e1;
    flex-shrink: 0;
  }

  .character-name-input :deep(.el-input__inner) {
    height: 21px;
    color: #160211;
    font-family: Inter, 'Source Han Sans CN VF', 'Noto Sans SC', sans-serif;
    font-size: 14px;
    font-weight: 400;
    line-height: 21px;
  }

  .character-name-input :deep(.el-input__inner::placeholder),
  .character-description-input :deep(.el-textarea__inner::placeholder) {
    color: #bbb;
  }

  .character-description-input :deep(.el-textarea__inner) {
    width: 100%;
    height: 64px;
    min-height: 64px !important;
    box-sizing: border-box;
    padding: 10px 12px;
    border: 0;
    border-radius: 14px;
    background: #fff;
    box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.12);
    color: #160211;
    font-family: Inter, 'Source Han Sans CN VF', 'Noto Sans SC', sans-serif;
    font-size: 14px;
    font-weight: 400;
    line-height: 21px;
    resize: none;
  }

  .character-description-input :deep(.el-textarea__inner:focus) {
    box-shadow: inset 0 0 0 1px #6033e1;
  }

  .character-image-upload {
    width: 100%;
    margin-top: 6px;
    flex-shrink: 0;
  }

  .character-image-upload :deep(.el-upload) {
    width: 100%;
  }

  .character-image-upload :deep(.el-upload-dragger) {
    width: 100%;
    height: 128px;
    box-sizing: border-box;
    padding: 0;
    border: 2px dashed rgba(96, 51, 225, 0.3);
    border-radius: 14px;
    background: transparent;
  }

  .character-image-upload.is-uploaded :deep(.el-upload-dragger) {
    height: 179px;
    overflow: hidden;
    border: 1px solid rgba(96, 51, 225, 0.4);
    background: #fbf9ff;
  }

  .character-image-upload.is-uploading :deep(.el-upload-dragger) {
    cursor: wait;
    border-color: rgba(96, 51, 225, 0.4);
    background: #fbf9ff;
  }

  .character-image-upload :deep(.el-upload-dragger:hover),
  .character-image-upload :deep(.el-upload-dragger.is-dragover) {
    border-color: rgba(96, 51, 225, 0.3);
    background: transparent;
  }

  .character-image-upload.is-uploaded :deep(.el-upload-dragger:hover),
  .character-image-upload.is-uploaded :deep(.el-upload-dragger.is-dragover) {
    border-color: rgba(96, 51, 225, 0.4);
    background: #fbf9ff;
  }

  .upload-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 124px;
    overflow: hidden;
    gap: 8px;
  }

  .upload-content.has-image {
    position: relative;
    height: 179px;
    background: #fbf9ff;
    gap: 0;
  }

  .upload-icon-bubble {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: rgba(237, 232, 255, 0.6);
  }

  .upload-icon-bubble .svg-icon {
    width: 18px;
    height: 18px;
    fill: none;
    stroke: #6033e1;
    stroke-linecap: round;
    stroke-linejoin: round;
    stroke-width: 1.5;
  }

  .upload-loading-icon {
    animation: generation-spin 0.9s linear infinite;
  }

  .upload-caption {
    color: #999;
    font-family: Inter, 'Source Han Sans CN VF', 'Noto Sans SC', sans-serif;
    font-size: 13px;
    font-weight: 500;
    line-height: 19.5px;
    text-align: center;
  }

  .uploaded-preview-image {
    position: absolute;
    inset: 0;
    display: block;
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  .change-upload-button.el-button {
    position: absolute;
    right: 6px;
    bottom: 9px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 76px;
    height: 24px;
    min-height: 24px;
    margin: 0;
    padding: 0;
    border: 0;
    border-radius: 6px;
    background: #6033e1;
    color: #fff;
    cursor: pointer;
    font-family: 'Microsoft YaHei', 'Source Han Sans CN VF', 'Noto Sans SC', sans-serif;
    font-size: 12px;
    font-weight: 400;
    line-height: 22px;
  }

  .change-upload-button.el-button:hover,
  .change-upload-button.el-button:focus {
    border: 0;
    background: #6033e1;
    color: #fff;
  }

  .remove-upload-button.el-button {
    position: absolute;
    top: 9px;
    right: 9px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    min-height: 16px;
    margin: 0;
    padding: 0;
    border: 0;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.45);
    color: #fff;
    cursor: pointer;
  }

  .remove-upload-button.el-button:hover,
  .remove-upload-button.el-button:focus {
    border: 0;
    background: rgba(0, 0, 0, 0.45);
    color: #fff;
  }

  .remove-upload-button .remove-upload-icon {
    width: 10px;
    height: 10px;
    color: currentColor;
  }

  .style-picker-strip {
    display: flex;
    flex-wrap: wrap;
    width: 100%;
    height: auto;
    box-sizing: border-box;
    overflow: visible;
    margin-top: 6px;
    padding: 0;
    gap: 12px;
    flex-shrink: 0;
  }

  .style-card {
    display: flex;
    flex: 0 0 83px;
    flex-direction: column;
    align-items: center;
    height: 107px;
    box-sizing: border-box;
    overflow: hidden;
    padding: 0;
    border: 2px solid transparent;
    border-radius: 14px;
    background: transparent;
    color: inherit;
    cursor: pointer;
    font: inherit;
    flex-shrink: 0;
    -webkit-tap-highlight-color: transparent;
  }

  .style-card:active .style-thumb {
    transform: scale(0.98);
  }

  .style-card:hover,
  .style-card:focus-visible {
    border-color: rgba(96, 51, 225, 0.18);
    outline: none;
  }

  .style-thumb {
    display: block;
    flex: 0 0 auto;
    width: 85px;
    height: 85px;
    margin-top: 2px;
    overflow: hidden;
    border-radius: 10px 10px 0 0;
    transition: transform 0.16s ease;
  }

  .style-thumb img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .style-label {
    margin-top: 6px;
    color: #555;
    font-family: 'Source Han Sans CN VF', 'Noto Sans SC', 'Microsoft YaHei', sans-serif;
    font-size: 12px;
    font-weight: 500;
    line-height: 12px;
    text-align: center;
  }

  @media (min-width: 768px) {
    .character-modal-shell.is-text-mode .character-generation-tabs,
    .character-modal-shell.is-image-mode .character-generation-tabs {
      --segmented-tabs-background: #eff0f7;
      --segmented-tabs-gap: 0px;
      --segmented-tabs-indicator-shadow: none;
      --segmented-tabs-font-family:
        ABeeZee, 'Source Han Sans CN VF', 'Noto Sans SC', 'Microsoft YaHei', sans-serif;
      --segmented-tabs-font-size: 13px;
      --segmented-tabs-line-height: 14px;
      --segmented-tabs-icon-label-gap: 0px;
    }

    .character-modal-shell.is-text-mode .character-generation-tabs :deep(.segmented-tabs__icon),
    .character-modal-shell.is-image-mode .character-generation-tabs :deep(.segmented-tabs__icon) {
      display: none;
    }

    .character-modal-shell.is-text-mode .character-generation-tabs :deep(.segmented-tabs__label),
    .character-modal-shell.is-text-mode
      .character-generation-tabs
      :deep(.segmented-tabs__mobile-label),
    .character-modal-shell.is-image-mode .character-generation-tabs :deep(.segmented-tabs__label),
    .character-modal-shell.is-image-mode
      .character-generation-tabs
      :deep(.segmented-tabs__mobile-label) {
      transform: none;
    }

    .character-modal-shell.is-text-mode .name-field {
      display: none;
    }

    .character-modal-shell.is-text-mode .appearance-field {
      height: 148px;
    }

    .character-modal-shell.is-text-mode .appearance-field .character-modal-label {
      height: 16px;
      padding: 0;
      font-weight: 400;
    }

    .character-modal-shell.is-text-mode .appearance-field .character-description-input {
      margin-top: 12px;
    }

    .character-modal-shell.is-text-mode .character-description-input :deep(.el-textarea__inner) {
      height: 120px;
      min-height: 120px !important;
      padding: 10px;
      border-radius: 16px;
      box-shadow: inset 0 0 0 1px #eff0f7;
      font-size: 13px;
      line-height: 19.5px;
    }

    .character-modal-shell.is-text-mode
      .character-description-input
      :deep(.el-textarea__inner::placeholder) {
      color: #6e6a7c;
    }

    .character-modal-shell.is-text-mode .style-field {
      width: 100%;
      height: auto;
      margin-left: 4px;
    }

    .character-modal-shell.is-text-mode .style-field .character-modal-label {
      height: 14px;
      padding: 0;
      color: #160211;
      font-family: 'PingFang SC', 'Source Han Sans CN VF', 'Noto Sans SC', sans-serif;
      font-size: 13px;
      font-weight: 500;
      line-height: 14px;
    }

    .character-modal-shell.is-text-mode .style-picker-strip {
      width: 100%;
      height: auto;
      margin: 12px 0 0;
      padding: 0;
      overflow: visible;
      gap: 12px;
    }

    .character-modal-shell.is-text-mode .style-card {
      width: 85px;
      height: 107px;
      overflow: visible;
      border: 0;
      border-radius: 0;
      flex: none;
    }

    .character-modal-shell.is-text-mode .style-card:hover,
    .character-modal-shell.is-text-mode .style-card:focus-visible {
      border-color: transparent;
    }

    .character-modal-shell.is-text-mode .style-thumb {
      width: 85px;
      height: 85px;
      margin-top: 0;
      border-radius: 16px;
    }

    .character-modal-shell.is-text-mode .style-card.selected .style-thumb,
    .character-modal-shell.is-text-mode .style-card:hover .style-thumb,
    .character-modal-shell.is-text-mode .style-card:focus-visible .style-thumb {
      position: relative;
    }

    .character-modal-shell.is-text-mode .style-card.selected .style-thumb::after,
    .character-modal-shell.is-text-mode .style-card:hover .style-thumb::after,
    .character-modal-shell.is-text-mode .style-card:focus-visible .style-thumb::after {
      position: absolute;
      z-index: 1;
      inset: 0;
      border-radius: inherit;
      box-shadow: inset 0 0 0 2px #6033e1;
      content: '';
      pointer-events: none;
    }

    .character-modal-shell.is-text-mode .style-label {
      margin-top: 8px;
      color: #160211;
      font-family: 'PingFang SC', 'Source Han Sans CN VF', 'Noto Sans SC', sans-serif;
      font-size: 13px;
      font-weight: 400;
      line-height: 14px;
    }

    .character-modal-shell.is-text-mode .style-label .desktop-copy {
      display: none;
    }

    .character-modal-shell.is-text-mode .style-label .mobile-copy {
      display: inline;
    }

    .character-modal-shell.is-image-mode .character-modal-body {
      height: 414px;
    }

    .character-modal-shell.is-image-mode .character-modal-body-content {
      padding: 15px 21px 20px;
      gap: 15px;
    }

    .character-modal-shell.is-image-mode .name-field {
      display: none;
    }

    .character-modal-shell.is-image-mode .image-upload-field {
      height: 171px;
      padding-top: 2px;
    }

    .character-modal-shell.is-image-mode .image-upload-field .character-modal-label {
      height: 17px;
      padding: 0;
      font-family: 'Source Han Sans CN VF', 'Noto Sans SC', 'Microsoft YaHei', sans-serif;
      font-size: 13px;
      font-weight: 500;
      line-height: 19.5px;
    }

    .character-modal-shell.is-image-mode .character-image-upload {
      margin-top: 12px;
    }

    .character-modal-shell.is-image-mode .character-image-upload :deep(.el-upload-dragger),
    .character-modal-shell.is-image-mode .character-image-upload :deep(.el-upload-dragger:hover),
    .character-modal-shell.is-image-mode
      .character-image-upload
      :deep(.el-upload-dragger.is-dragover) {
      height: 146px;
      padding: 10px;
      border: 0;
      border-radius: 16px;
      background: rgba(239, 240, 247, 0.5);
    }

    .character-modal-shell.is-image-mode .upload-content,
    .character-modal-shell.is-image-mode .upload-content.has-image {
      height: 126px;
      background: transparent;
      gap: 8px;
    }

    .character-modal-shell.is-image-mode .image-upload-field.has-uploaded-image {
      width: 464px;
    }

    .character-modal-shell.is-image-mode
      .character-image-upload.is-uploaded
      :deep(.el-upload-dragger),
    .character-modal-shell.is-image-mode
      .character-image-upload.is-uploaded
      :deep(.el-upload-dragger:hover),
    .character-modal-shell.is-image-mode
      .character-image-upload.is-uploaded
      :deep(.el-upload-dragger.is-dragover) {
      height: 140px;
      padding: 0;
      border: 0;
      border-radius: 16px;
      background: rgba(239, 240, 247, 0.5);
    }

    .character-modal-shell.is-image-mode
      .character-image-upload.is-uploaded
      .upload-content.has-image {
      position: relative;
      height: 140px;
      background: transparent;
      gap: 0;
    }

    .character-modal-shell.is-image-mode
      .character-image-upload.is-uploaded
      .uploaded-preview-image {
      position: absolute;
      top: 8px;
      left: 50%;
      width: 78px;
      height: 124px;
      transform: translateX(-50%);
      object-fit: cover;
    }

    .character-modal-shell.is-image-mode .change-upload-button.el-button {
      right: 8px;
      bottom: 8px;
      width: 58px;
      height: 32px;
      min-height: 32px;
      padding: 0;
      border: 1px solid #eff0f7;
      border-radius: 16px;
      background: #fff;
      color: #160211;
      font-family: 'PingFang SC', 'Source Han Sans CN VF', 'Noto Sans SC', sans-serif;
      font-size: 13px;
      font-weight: 500;
      line-height: 18px;
    }

    .character-modal-shell.is-image-mode .change-upload-button.el-button:hover,
    .character-modal-shell.is-image-mode .change-upload-button.el-button:focus {
      border-color: #eff0f7;
      background: #fff;
      color: #160211;
    }

    .character-modal-shell.is-image-mode .remove-upload-button.el-button {
      top: 8px;
      right: 8px;
      width: 32px;
      height: 32px;
      min-height: 32px;
      border: 1px solid #eff0f7;
      border-radius: 16px;
      background: #fff;
      color: #160211;
    }

    .character-modal-shell.is-image-mode .remove-upload-button.el-button:hover,
    .character-modal-shell.is-image-mode .remove-upload-button.el-button:focus {
      border-color: #eff0f7;
      background: #fff;
      color: #160211;
    }

    .character-modal-shell.is-image-mode .remove-upload-icon {
      width: 16px;
      height: 16px;
      color: currentColor;
    }

    .character-modal-shell.is-image-mode .upload-icon-bubble {
      width: 40px;
      height: 40px;
      background: #ede8ff;
    }

    .character-modal-shell.is-image-mode .upload-icon-bubble .svg-icon {
      width: 16px;
      height: 16px;
      stroke-width: 1.4;
    }

    .character-modal-shell.is-image-mode .upload-caption {
      color: #160211;
      font-family: 'PingFang SC', 'Source Han Sans CN VF', 'Noto Sans SC', sans-serif;
      font-size: 13px;
      font-weight: 400;
      line-height: 14px;
    }

    .character-modal-shell.is-image-mode .mobile-image-examples {
      display: flex;
      flex-direction: column;
      width: 360px;
      max-width: 100%;
      height: 129px;
      gap: 12px;
      flex: 0 0 auto;
    }

    .character-modal-shell.is-image-mode .mobile-section-title {
      margin: 0;
      color: #160211;
      font-family: 'PingFang SC', 'Source Han Sans CN VF', 'Noto Sans SC', sans-serif;
      font-size: 13px;
      font-weight: 500;
      line-height: 14px;
    }

    .character-modal-shell.is-image-mode .mobile-example-grid {
      display: grid;
      width: 418px;
      max-width: 100%;
      grid-template-columns: 81px 81px 110px 110px;
      gap: 12px;
    }

    .character-modal-shell.is-image-mode .mobile-example-card {
      display: flex;
      flex-direction: column;
      align-items: center;
      width: 81px;
      gap: 8px;
    }

    .character-modal-shell.is-image-mode .mobile-example-card.is-wide {
      width: 110px;
    }

    .character-modal-shell.is-image-mode .mobile-example-thumb,
    .character-modal-shell.is-image-mode .mobile-example-thumb img {
      display: block;
      width: 81px;
      height: 81px;
      border-radius: 16px;
    }

    .character-modal-shell.is-image-mode .mobile-example-thumb.is-wide,
    .character-modal-shell.is-image-mode .mobile-example-thumb.is-wide img {
      width: 110px;
      height: 81px;
    }

    .character-modal-shell.is-image-mode .mobile-example-thumb {
      overflow: hidden;
      background: #f5f0ff;
    }

    .character-modal-shell.is-image-mode .mobile-example-thumb img {
      object-fit: cover;
    }

    .character-modal-shell.is-image-mode .mobile-example-thumb.is-blurry img {
      filter: blur(3px);
      transform: scale(1.07);
    }

    .character-modal-shell.is-image-mode .mobile-example-caption {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 81px;
      color: #160211;
      font-family: 'PingFang SC', 'Source Han Sans CN VF', 'Noto Sans SC', sans-serif;
      font-size: 13px;
      font-weight: 400;
      line-height: 14px;
      gap: 4px;
      white-space: nowrap;
    }

    .character-modal-shell.is-image-mode .mobile-example-card.is-wide .mobile-example-caption {
      width: 110px;
    }

    .character-modal-shell.is-image-mode .mobile-example-caption .svg-icon {
      width: 10px;
      height: 10px;
      color: #51da68;
      fill: none;
      stroke: currentColor;
      stroke-linecap: round;
      stroke-linejoin: round;
      stroke-width: 1.4;
    }

    .character-modal-shell.is-image-mode .mobile-example-caption.invalid .svg-icon {
      color: #ff4b60;
    }

    .character-modal-shell.is-image-mode .supplement-description-field {
      width: 100%;
      gap: 12px;
    }

    .character-modal-shell.is-image-mode .supplement-description-label {
      height: 14px;
      font-weight: 500;
    }

    .character-modal-shell.is-image-mode .supplement-description-title {
      font-family: 'PingFang SC', 'Source Han Sans CN VF', 'Noto Sans SC', sans-serif;
      font-size: 13px;
      font-weight: 500;
      line-height: 14px;
    }

    .character-modal-shell.is-image-mode .optional-tag {
      color: #999;
      font-weight: 400;
    }

    .character-modal-shell.is-image-mode .supplement-description-tip {
      color: #999;
      font-family: 'PingFang SC', 'Source Han Sans CN VF', 'Noto Sans SC', sans-serif;
      font-size: 11px;
      font-weight: 400;
      line-height: 14px;
    }

    .character-modal-shell.is-image-mode
      .supplement-description-input
      :deep(.el-textarea__inner) {
      height: 64px;
      min-height: 64px !important;
      padding: 10px 12px;
      border-radius: 16px;
      box-shadow: inset 0 0 0 1px #eff0f7;
      font-family: 'PingFang SC', 'Source Han Sans CN VF', 'Noto Sans SC', sans-serif;
      font-size: 13px;
      line-height: 19.5px;
    }

    .character-modal-shell.is-image-mode
      .supplement-description-input
      :deep(.el-textarea__inner::placeholder) {
      color: #6e6a7c;
    }

    .character-modal-shell.is-image-mode .style-field {
      width: 473px;
      height: auto;
    }

    .character-modal-shell.is-image-mode .style-field .character-modal-label {
      height: 14px;
      padding: 0;
      color: #160211;
      font-family: 'PingFang SC', 'Source Han Sans CN VF', 'Noto Sans SC', sans-serif;
      font-size: 13px;
      font-weight: 500;
      line-height: 14px;
    }

    .character-modal-shell.is-image-mode .style-picker-strip {
      width: 100%;
      height: auto;
      margin: 12px 0 0;
      padding: 0;
      overflow: visible;
      gap: 12px;
    }

    .character-modal-shell.is-image-mode .style-card {
      width: 85px;
      height: 107px;
      overflow: visible;
      border: 0;
      border-radius: 0;
      flex: none;
    }

    .character-modal-shell.is-image-mode .style-card:hover,
    .character-modal-shell.is-image-mode .style-card:focus-visible {
      border-color: transparent;
    }

    .character-modal-shell.is-image-mode .style-thumb {
      width: 85px;
      height: 85px;
      margin-top: 0;
      border-radius: 16px;
    }

    .character-modal-shell.is-image-mode .style-card.selected .style-thumb,
    .character-modal-shell.is-image-mode .style-card:hover .style-thumb,
    .character-modal-shell.is-image-mode .style-card:focus-visible .style-thumb {
      position: relative;
    }

    .character-modal-shell.is-image-mode .style-card.selected .style-thumb::after,
    .character-modal-shell.is-image-mode .style-card:hover .style-thumb::after,
    .character-modal-shell.is-image-mode .style-card:focus-visible .style-thumb::after {
      position: absolute;
      z-index: 1;
      inset: 0;
      border-radius: inherit;
      box-shadow: inset 0 0 0 2px #6033e1;
      content: '';
      pointer-events: none;
    }

    .character-modal-shell.is-image-mode .style-label {
      margin-top: 8px;
      color: #160211;
      font-family: 'PingFang SC', 'Source Han Sans CN VF', 'Noto Sans SC', sans-serif;
      font-size: 13px;
      font-weight: 400;
      line-height: 14px;
    }

    .character-modal-shell.is-image-mode .style-label .desktop-copy {
      display: none;
    }

    .character-modal-shell.is-image-mode .style-label .mobile-copy {
      display: inline;
    }
  }

  .character-modal-footer {
    width: 100%;
    height: 86px;
    box-sizing: border-box;
    margin-top: 0;
    padding: 16px 24px 26px;
    flex-shrink: 0;
  }

  .character-generation-body {
    position: relative;
    height: 548px;
    box-sizing: border-box;
    padding: 0;
  }

  .generated-preview-card {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 191px;
    height: 278px;
    margin-left: 156px;
    overflow: hidden;
    border: 1px solid rgba(0, 0, 0, 0.1);
    border-radius: 12px;
    background: #fff;
    flex-shrink: 0;
  }

  .generated-preview-card.is-loading {
    top: 9px;
    width: 478px;
    max-width: calc(100% - 42px);
    height: 241px;
    margin-left: 26px;
    border: 0;
    border-radius: 16px;
    background: rgba(239, 240, 247, 0.5);
  }

  .generated-preview-card.is-loading + .generation-fields {
    margin-top: 17px;
  }

  .generated-preview-image {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .generation-loading-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
  }

  .generation-loading-icon {
    display: block;
    width: 24px;
    height: 24px;
    animation: generation-spin 0.9s linear infinite;
  }

  .generation-loading-text {
    margin-top: 16px;
    color: #6e6a7c;
    font-family: 'Source Han Sans CN VF', 'Noto Sans SC', 'Microsoft YaHei', sans-serif;
    font-size: 13px;
    font-weight: 400;
    line-height: 14px;
  }

  .generation-loading-help {
    margin-top: 12px;
    color: #999;
    font-family: 'Source Han Sans CN VF', 'Noto Sans SC', 'Microsoft YaHei', sans-serif;
    font-size: 13px;
    font-weight: 400;
    line-height: 14px;
  }

  .generation-fields {
    width: 472px;
    max-width: 100%;
    margin-top: -1px;
    margin-left: 32px;
  }

  .generation-name-field {
    height: 63px;
  }

  .generation-name-field .character-modal-label {
    height: 25px;
  }

  .generation-setting-field {
    height: 197px;
    box-sizing: border-box;
    margin-top: 0;
    padding-top: 16px;
  }

  .generation-setting-field .character-modal-label {
    height: 23px;
    padding-bottom: 3px;
  }

  .generation-setting-label {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    width: 100%;
    font-weight: 400;
  }

  .generation-setting-title {
    flex: 0 0 auto;
  }

  .generation-setting-tip {
    align-items: center;
    color: #6e6a7c;
    font-size: 11px;
    font-weight: 400;
    line-height: 17px;
    white-space: nowrap;
  }

  .generation-setting-tip.desktop-copy {
    display: inline-flex;
  }

  .generation-setting-tip-icon {
    width: 16px;
    height: 16px;
  }

  .generation-description-input :deep(.el-textarea__inner) {
    height: 85px;
    min-height: 85px !important;
  }

  .generation-description-input :deep(.el-textarea__inner::placeholder) {
    color: #999;
  }

  .generation-actions {
    display: flex;
    justify-content: flex-end;
    width: 461px;
    margin-top: 21px;
    margin-left: -1px;
    gap: 10px;
  }

  .regenerate-character-button.el-button,
  .confirm-generation-button.el-button {
    height: 40px;
    margin: 0;
    border-radius: 6px;
    font-family: 'Microsoft YaHei', 'Source Han Sans CN VF', 'Noto Sans SC', sans-serif;
    font-size: 14px;
    font-weight: 400;
    line-height: 22px;
  }

  .regenerate-character-button.el-button {
    width: 102px;
    border-color: #e5e7eb;
    background: #fff;
    color: #333;
  }

  .confirm-generation-button.el-button {
    width: 76px;
    border: 0;
    background: #6033e1;
    color: #fff;
  }

  .confirm-generation-button.el-button:hover,
  .confirm-generation-button.el-button:focus {
    background: #6033e1;
    color: #fff;
  }

  .regenerate-character-button.el-button.is-disabled,
  .regenerate-character-button.el-button.is-disabled:hover,
  .regenerate-character-button.el-button.is-disabled:focus {
    border-color: #e5e7eb;
    background: #fff;
    color: #333;
    opacity: 0.5;
  }

  .confirm-generation-button.el-button.is-disabled,
  .confirm-generation-button.el-button.is-disabled:hover,
  .confirm-generation-button.el-button.is-disabled:focus {
    border: 0;
    background: #6033e1;
    color: #fff;
    opacity: 1;
  }

  @keyframes generation-spin {
    to {
      transform: rotate(360deg);
    }
  }

  .generate-character-button.el-button {
    width: 472px;
    max-width: 100%;
    height: 44px;
    margin: 0;
    border: 0;
    border-radius: 14px;
    background: #6033e1;
    color: #fff;
    font-family: 'Source Han Sans CN VF', 'Noto Sans SC', 'Microsoft YaHei', sans-serif;
    font-size: 14px;
    font-weight: 500;
    line-height: 21px;
    gap: 8px;
  }

  .generate-character-button.el-button:hover,
  .generate-character-button.el-button:focus {
    background: #6033e1;
    color: #fff;
  }

  .generate-character-button.el-button.is-disabled,
  .generate-character-button.el-button.is-disabled:hover,
  .generate-character-button.el-button.is-disabled:focus {
    background: #e8e4f5;
    color: #b0a0d8;
    opacity: 1;
  }

  .generate-character-button.el-button :deep(span) {
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }

  .generate-character-button.el-button .desktop-copy {
    display: inline-flex;
  }

  .generate-character-button.el-button .mobile-copy {
    display: none;
  }

  .ai-cost-icon {
    width: 12px;
    height: 12px;
    flex: 0 0 auto;
  }

  .credit-count {
    color: #fff;
    font-family: 'Source Han Sans CN VF', 'Noto Sans SC', 'Microsoft YaHei', sans-serif;
    font-size: 13px;
    font-weight: 900;
    line-height: 14px;
  }

  @media (max-width: 767px) {
    .desktop-copy {
      display: none;
    }

    .mobile-copy {
      display: inline;
    }

    :global(.character-dialog-mask) {
      background: rgba(0, 0, 0, 0.3);
    }

    :global(.character-dialog.el-dialog) {
      width: 100vw;
      max-width: none;
      height: calc(100dvh - 72px);
      max-height: none;
      align-self: flex-end;
      overflow: hidden;
      margin: 0 !important;
      border-radius: 16px 16px 0 0;
      box-shadow: none;
    }

    @supports not (height: 100dvh) {
      :global(.character-dialog.el-dialog) {
        height: calc(100vh - 72px);
      }
    }

    :global(.character-dialog.el-dialog .el-dialog__body) {
      height: 100%;
    }

    .character-modal-shell {
      display: flex;
      width: 100%;
      height: 100%;
      flex-direction: column;
      position: relative;
      overflow: hidden;
    }

    .character-modal-shell.is-generation .character-modal-header,
    .character-modal-header {
      align-items: flex-start;
      justify-content: flex-start;
      height: 40px;
      padding: 16px 16px 0;
      gap: 7px;
      flex: 0 0 auto;
    }

    .character-modal-heading {
      width: auto;
      height: 24px;
    }

    .character-modal-header-actions {
      display: contents;
    }

    .membership-upgrade-button {
      justify-content: flex-start;
      width: 132px;
      height: 27px;
      padding: 0 13px 0 16px;
      border-radius: 14px;
      gap: 7px;
    }

    .character-modal-title {
      font-family: 'PingFang SC', 'Source Han Sans CN VF', 'Noto Sans SC', sans-serif;
      font-size: 13px;
      line-height: 16px;
    }

    .character-modal-close {
      width: 44px;
      height: 24px;
      margin-left: auto;
      border: 1px solid #eff0f7;
      border-radius: 66px;
      background: #fff;
      color: #160211;
    }

    .character-modal-close:hover,
    .character-modal-close:focus-visible {
      background: #fff;
    }

    .character-modal-close .svg-icon {
      display: none;
    }

    .character-modal-close .collapse-icon {
      display: block;
      width: 12px;
      height: 12px;
      fill: none;
      stroke: currentColor;
      stroke-linecap: round;
      stroke-linejoin: round;
      stroke-width: 1.4;
    }

    .character-modal-close::before {
      content: none;
    }

    .character-modal-tabs {
      padding: 16px 16px 0;
    }

    .character-modal-body {
      height: auto;
      min-height: 0;
      overflow: hidden;
      flex: 1 1 auto;
    }

    .character-modal-body-content {
      padding: 16px;
      gap: 16px;
    }

    .character-modal-field {
      width: 100%;
    }

    .name-field {
      display: none;
    }

    .appearance-field,
    .image-upload-field,
    .image-upload-field.has-uploaded-image,
    .supplement-description-field,
    .style-field {
      height: auto;
      padding-top: 0;
      gap: 12px;
    }

    .character-modal-label,
    .style-field .character-modal-label,
    .image-upload-field .character-modal-label,
    .supplement-description-label,
    .generation-name-field .character-modal-label,
    .generation-setting-field .character-modal-label {
      height: 14px;
      padding: 0;
      font-family: 'PingFang SC', 'Source Han Sans CN VF', 'Noto Sans SC', sans-serif;
      font-size: 13px;
      line-height: 14px;
    }

    .supplement-description-label {
      display: flex;
      align-items: center;
      justify-content: space-between;
      height: auto;
      min-height: 14px;
      gap: 8px;
    }

    .supplement-description-title {
      font-family: 'PingFang SC', 'Source Han Sans CN VF', 'Noto Sans SC', sans-serif;
      font-size: 13px;
      font-weight: 500;
      line-height: 14px;
    }

    .supplement-description-tip {
      font-family: 'PingFang SC', 'Source Han Sans CN VF', 'Noto Sans SC', sans-serif;
      font-size: 11px;
      line-height: 14px;
    }

    .supplement-description-input :deep(.el-textarea__inner) {
      height: 90px;
      min-height: 90px !important;
      padding: 10px;
      border-radius: 16px;
      box-shadow: inset 0 0 0 1px #eff0f7;
      font-family: 'PingFang SC', 'Source Han Sans CN VF', 'Noto Sans SC', sans-serif;
      font-size: 13px;
      line-height: 19.5px;
    }

    .supplement-description-input :deep(.el-textarea__inner::placeholder) {
      color: #6e6a7c;
    }

    .required {
      display: none;
    }

    .character-name-input :deep(.el-input__wrapper) {
      height: 42px;
      min-height: 42px;
      border-radius: 16px;
      box-shadow: inset 0 0 0 1px #eff0f7;
    }

    .character-description-input :deep(.el-textarea__inner) {
      height: 120px;
      min-height: 120px !important;
      padding: 10px;
      border-radius: 16px;
      box-shadow: inset 0 0 0 1px #eff0f7;
      font-family: 'PingFang SC', 'Source Han Sans CN VF', 'Noto Sans SC', sans-serif;
      font-size: 13px;
      line-height: 19.5px;
    }

    .character-description-input :deep(.el-textarea__inner::placeholder) {
      color: #6e6a7c;
    }

    .character-image-upload {
      margin-top: 0;
    }

    .character-image-upload :deep(.el-upload-dragger) {
      height: 120px;
      border-color: #eff0f7;
      border-radius: 16px;
    }

    .character-image-upload.is-uploaded :deep(.el-upload-dragger),
    .upload-content.has-image {
      height: 180px;
    }

    .character-modal-shell.is-image-mode .character-modal-body-content {
      padding-bottom: 16px;
      gap: 16px;
    }

    .character-modal-shell.is-image-mode .image-upload-field {
      gap: 0;
    }

    .character-modal-shell.is-image-mode .image-upload-label {
      display: none;
    }

    .character-modal-shell.is-image-mode .character-image-upload :deep(.el-upload-dragger) {
      height: 160px;
      border: 0;
      border-radius: 16px;
      background: rgba(239, 240, 247, 0.5);
    }

    .character-modal-shell.is-image-mode
      .character-image-upload.is-uploaded
      :deep(.el-upload-dragger),
    .character-modal-shell.is-image-mode .upload-content,
    .character-modal-shell.is-image-mode .upload-content.has-image {
      height: 160px;
    }

    .character-modal-shell.is-image-mode .upload-content {
      gap: 8px;
    }

    .character-modal-shell.is-image-mode .upload-icon-bubble {
      width: 40px;
      height: 40px;
      background: #ede8ff;
    }

    .character-modal-shell.is-image-mode .upload-icon-bubble .svg-icon {
      width: 16px;
      height: 16px;
      stroke-width: 1.4;
    }

    .character-modal-shell.is-image-mode .upload-caption {
      color: #160211;
      font-family: 'PingFang SC', 'Source Han Sans CN VF', 'Noto Sans SC', sans-serif;
      font-size: 13px;
      font-weight: 400;
      line-height: 14px;
    }

    .character-modal-shell.is-image-mode .uploaded-preview-image {
      position: static;
      width: 90px;
      height: 144px;
      object-fit: cover;
    }

    .character-modal-shell.is-image-mode .change-upload-button.el-button {
      right: 8px;
      bottom: 8px;
      width: 58px;
      height: 32px;
      min-height: 32px;
      border: 1px solid #eff0f7;
      border-radius: 16px;
      background: #fff;
      color: #160211;
      font-family: 'PingFang SC', 'Source Han Sans CN VF', 'Noto Sans SC', sans-serif;
      font-size: 13px;
      font-weight: 500;
      line-height: 18px;
    }

    .character-modal-shell.is-image-mode .change-upload-button.el-button:hover,
    .character-modal-shell.is-image-mode .change-upload-button.el-button:focus {
      border-color: #eff0f7;
      background: #fff;
      color: #160211;
    }

    .character-modal-shell.is-image-mode .remove-upload-button.el-button {
      top: 8px;
      right: 8px;
      width: 32px;
      height: 32px;
      min-height: 32px;
      border: 1px solid #eff0f7;
      border-radius: 16px;
      background: #fff;
      color: #160211;
    }

    .character-modal-shell.is-image-mode .remove-upload-button.el-button:hover,
    .character-modal-shell.is-image-mode .remove-upload-button.el-button:focus {
      border-color: #eff0f7;
      background: #fff;
      color: #160211;
    }

    .character-modal-shell.is-image-mode .remove-upload-icon {
      display: block;
      width: 16px;
      height: 16px;
      color: currentColor;
    }

    .mobile-image-examples {
      display: flex;
      flex-direction: column;
      width: 100%;
      gap: 12px;
      flex: 0 0 auto;
    }

    .mobile-section-title {
      margin: 0;
      color: #160211;
      font-family: 'PingFang SC', 'Source Han Sans CN VF', 'Noto Sans SC', sans-serif;
      font-size: 13px;
      font-weight: 500;
      line-height: 14px;
    }

    .mobile-example-grid {
      display: grid;
      width: 100%;
      max-width: 360px;
      grid-template-columns: repeat(auto-fit, minmax(min(72px, 100%), 1fr));
      gap: 12px;
    }

    .mobile-example-card {
      display: flex;
      flex-direction: column;
      align-items: center;
      width: 100%;
      min-width: 0;
      gap: 8px;
    }

    .mobile-example-thumb {
      display: block;
      width: min(81px, 100%);
      height: auto;
      aspect-ratio: 1;
      border-radius: 16px;
    }

    .mobile-example-thumb img {
      display: block;
      width: 100%;
      height: 100%;
      border-radius: inherit;
    }

    .mobile-example-thumb {
      overflow: hidden;
      background: #f5f0ff;
    }

    .mobile-example-thumb img {
      object-fit: cover;
    }

    .mobile-example-thumb.is-blurry img {
      filter: blur(3px);
      transform: scale(1.07);
    }

    .mobile-example-caption {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      min-width: 0;
      color: #160211;
      font-family: 'PingFang SC', 'Source Han Sans CN VF', 'Noto Sans SC', sans-serif;
      font-size: 13px;
      font-weight: 400;
      line-height: 14px;
      gap: 4px;
      white-space: nowrap;
    }

    .mobile-example-card.is-wide .mobile-example-caption {
      width: 110px;
    }

    .mobile-example-caption .svg-icon {
      width: 10px;
      height: 10px;
      color: #51da68;
      fill: none;
      stroke: currentColor;
      stroke-linecap: round;
      stroke-linejoin: round;
      stroke-width: 1.4;
    }

    .mobile-example-caption.invalid .svg-icon {
      color: #ff4b60;
    }

    .mobile-image-divider {
      display: block;
      width: 100%;
      height: 1px;
      background: #eff0f7;
      flex: 0 0 auto;
    }

    .style-picker-strip {
      display: grid;
      width: 100%;
      max-width: none;
      height: auto;
      margin: 0;
      padding: 0;
      overflow: visible;
      grid-template-columns: repeat(auto-fit, minmax(min(72px, 100%), 1fr));
      gap: 12px;
    }

    .character-modal-shell.is-image-mode .style-field {
      gap: 12px;
    }

    .character-modal-shell.is-image-mode .style-field .character-modal-label {
      height: 14px;
      color: #160211;
      font-weight: 500;
      line-height: 14px;
    }

    .character-modal-shell.is-image-mode .style-picker-strip {
      row-gap: 12px;
    }

    .style-card {
      width: 100%;
      min-width: 0;
      height: 103px;
      overflow: visible;
      border: 0;
      border-radius: 0;
      flex: none;
    }

    .style-card:hover,
    .style-card:focus-visible {
      border-color: transparent;
    }

    .style-thumb {
      width: min(81px, 100%);
      height: auto;
      aspect-ratio: 1;
      margin-top: 0;
      border-radius: 16px;
    }

    .style-card.selected .style-thumb,
    .style-card:hover .style-thumb,
    .style-card:focus-visible .style-thumb {
      position: relative;
    }

    .style-card.selected .style-thumb::after,
    .style-card:hover .style-thumb::after,
    .style-card:focus-visible .style-thumb::after {
      position: absolute;
      z-index: 1;
      inset: 0;
      border-radius: inherit;
      box-shadow: inset 0 0 0 2px #6033e1;
      content: '';
      pointer-events: none;
    }

    .style-label {
      margin-top: 8px;
      color: #160211;
      font-family: 'PingFang SC', 'Source Han Sans CN VF', 'Noto Sans SC', sans-serif;
      font-size: 13px;
      font-weight: 400;
      line-height: 14px;
    }

    .character-modal-footer {
      height: 74px;
      box-sizing: border-box;
      margin-top: 0;
      padding: 16px;
      background: #fff;
      flex: 0 0 auto;
    }

    .character-modal-shell.is-image-mode .character-modal-footer {
      position: static;
      background: #fff;
    }

    .generate-character-button.el-button {
      width: 100%;
      height: 42px;
      border-radius: 16px;
      font-family: 'PingFang SC', 'Source Han Sans CN VF', 'Noto Sans SC', sans-serif;
      font-size: 13px;
      line-height: 18px;
    }

    .character-modal-shell.is-image-mode .generate-character-button.el-button.is-disabled,
    .character-modal-shell.is-image-mode .generate-character-button.el-button.is-disabled:hover,
    .character-modal-shell.is-image-mode .generate-character-button.el-button.is-disabled:focus {
      background: #6033e1;
      color: #fff;
      opacity: 0.5;
    }

    .generate-character-button.el-button .desktop-copy {
      display: none;
    }

    .generate-character-button.el-button .mobile-copy {
      display: inline-flex;
    }

    .character-generation-body {
      display: flex;
      flex-direction: column;
      gap: 16px;
      position: static;
      height: auto;
      min-height: 0;
      padding: 16px 16px 90px;
      overflow-y: auto;
      flex: 1 1 auto;
    }

    .generated-preview-card {
      top: 0;
      width: 100%;
      max-width: none;
      height: 420px;
      margin: 0;
      border: 0;
      border-radius: 16px;
      background: rgba(239, 240, 247, 0.5);
    }

    .generated-preview-card.is-loading {
      height: 241px;
    }

    .generated-preview-card.is-loading + .generation-fields {
      margin-top: 0;
    }

    .generation-loading-state {
      gap: 12px;
    }

    .generation-loading-text,
    .generation-loading-help {
      margin-top: 0;
      font-family: 'PingFang SC', 'Source Han Sans CN VF', 'Noto Sans SC', sans-serif;
      font-size: 13px;
      line-height: 14px;
    }

    .generation-loading-text {
      color: #6e6a7c;
    }

    .generation-loading-help {
      color: #999;
    }

    .generation-fields {
      width: 100%;
      margin: 0;
    }

    .generation-name-field,
    .generation-setting-field {
      height: auto;
      padding-top: 0;
      gap: 12px;
    }

    .character-modal-shell.is-generation .generation-name-field .required {
      display: inline;
    }

    .character-modal-shell.is-generation .generation-setting-field .required {
      display: inline;
    }

    .character-modal-shell.is-generation .generation-setting-field .desktop-required {
      display: none;
    }

    .character-modal-shell.is-generation .character-modal-label {
      font-weight: 400;
    }

    .character-modal-shell.is-generation .character-name-input :deep(.el-input__wrapper) {
      border-radius: 12px;
      padding: 10px 16px;
    }

    .character-modal-shell.is-generation .character-name-input :deep(.el-input__inner) {
      height: 14px;
      font-family: 'PingFang SC', 'Source Han Sans CN VF', 'Noto Sans SC', sans-serif;
      font-size: 13px;
      line-height: 14px;
    }

    .generation-setting-field {
      margin-top: 16px;
    }

    .generation-setting-label {
      justify-content: flex-start;
    }

    .generation-setting-label .generation-setting-tip.mobile-copy {
      display: inline;
      color: #6e6a7c;
      font-size: 13px;
      line-height: 14px;
    }

    .generation-setting-label .mobile-required {
      line-height: 14px;
    }

    .generation-description-input :deep(.el-textarea__inner) {
      height: 90px;
      min-height: 90px !important;
      border-radius: 12px;
      padding: 10px 16px;
    }

    .generation-description-input :deep(.el-textarea__inner::placeholder) {
      color: #6e6a7c;
    }

    .generation-actions {
      position: absolute;
      right: auto;
      bottom: 16px;
      left: 16px;
      z-index: 3;
      width: min(360px, calc(100% - 32px));
      height: 42px;
      margin: 0;
      gap: 8px;
      opacity: 1;
    }

    .generation-actions.is-disabled {
      opacity: 0.5;
    }

    .regenerate-character-button.el-button,
    .confirm-generation-button.el-button {
      flex: 1 1 0;
      width: auto;
      height: 42px;
      min-height: 42px;
      padding: 10px 16px;
      border-radius: 16px;
      font-family: 'PingFang SC', 'Source Han Sans CN VF', 'Noto Sans SC', sans-serif;
      font-size: 13px;
      font-weight: 500;
      line-height: 18px;
    }

    .regenerate-character-button.el-button,
    .regenerate-character-button.el-button:hover,
    .regenerate-character-button.el-button:focus,
    .regenerate-character-button.el-button.is-disabled,
    .regenerate-character-button.el-button.is-disabled:hover,
    .regenerate-character-button.el-button.is-disabled:focus {
      border-color: #eff0f7;
      background: #fff;
      color: #160211;
    }

    .confirm-generation-button.el-button,
    .confirm-generation-button.el-button:hover,
    .confirm-generation-button.el-button:focus,
    .confirm-generation-button.el-button.is-disabled,
    .confirm-generation-button.el-button.is-disabled:hover,
    .confirm-generation-button.el-button.is-disabled:focus {
      border-color: #6033e1;
      background: #6033e1;
      color: #fff;
    }

    .confirm-generation-button.el-button {
      width: auto;
    }
  }
</style>
